<script>
  import { format } from 'svelte-i18n';
  import { Alert, Popover } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { Switch } from '@client/components/switch';

  import { formatBytes } from '@common/utils/numbers';

  export let files = [];
  export let inline = false;
  export let uploadSuccess = false;
  export let maxFileSize = 3 * 1024 * 1024;
  export let onFileChange = () => {};
  export let onRemoveFile = () => {};
  export let inputId = 'image-bulk';
</script>

<div class="image-bulk-upload-form">
  <div class="section">
    <div class="asset-details">
      <div class="asset-item">
        <div class="file">
          <div class="asset-info">
            <b>{$format('label.BULK_MODAL_UPLOAD_FILES')}</b>
            <p>{$format('label.DESCRIPTION.BULK_MODAL_UPLOAD_FILES')}</p>
          </div>
          <div class="asset-actions">
            <label class="btn btn-secondary btn-sm" for={inputId}>{$format('label.CHOOSE_FILES')}</label>
            <span id="file-chosen">
              {#if !files.length}
                {$format('label.NO_FILE_CHOSEN')}
              {/if}
            </span>
            <input
              id={inputId}
              name={inputId}
              hidden
              type="file"
              bind:files
              on:change={onFileChange}
              accept=".png, .jpg, .jpeg, .gif"
              multiple
            />
          </div>
        </div>
      </div>
      {#if files.length}
        {#each Array.from(files) as file, index}
          <div class="asset-item preview">
            <div class="asset-info">
              <b>{index === 0 ? $format('label.PREVIEW_IMAGES') : ''}</b>
            </div>
            <div class="asset-actions">
              <div class="bulk-files">
                <div class="file-item">
                  <div class="image-preview">
                    <img
                      id="popover-{index}"
                      src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
                      style="background: url({URL.createObjectURL(file)}) no-repeat center center;"
                      alt={file.name}
                    />
                    <Popover class="bulk-image-popover" target="popover-{index}" placement="right" hideOnOutsideClick>
                      <img class="popover" src={URL.createObjectURL(file)} alt={file.name} />
                    </Popover>
                  </div>
                  {file.name} ({formatBytes(file.size)})
                  <div class="icon-actions">
                    {#if !uploadSuccess}
                      <Icon name="trash-2" class="pointer" on:click={() => onRemoveFile(index)} />
                    {:else}
                      <Icon name="check" />
                    {/if}
                  </div>
                </div>
              </div>
            </div>
            {#if file.size > maxFileSize}
              <Alert color="warning" class="mt-2 mb-0 px-3 py-2">
                <Icon name="alert-triangle" class="me-1" />
                <small>{$format('label.ASSET_IMAGE_SIZE_WARNING')}</small>
              </Alert>
            {/if}
          </div>
        {/each}
      {/if}
      <div class="asset-item">
        <div class="asset-info">
          <b>{$format('label.ASSET_IMAGE_OPTION_INLINE')}</b>
          <p>{$format('label.DESCRIPTION.ASSET_IMAGE_OPTION_INLINE')}</p>
        </div>
        <div class="asset-actions">
          <Switch bind:checked={inline} />
        </div>
      </div>
    </div>
  </div>
</div>

<style lang="scss">
  .image-bulk-upload-form {
    width: 100%;

    .section {
      margin-top: var(--margin-base);

      .heading {
        font-size: var(--font-size-large);
        font-weight: var(--font-weight-medium);
        margin-bottom: var(--margin-base);

        i {
          cursor: help;
          font-size: 0.8em;
        }
      }
    }

    .asset-details {
      border: 1px solid var(--border-color);
      border-radius: var(--border-radius-base);
      padding: var(--padding-small) var(--padding-base);

      .asset-item {
        display: flex;
        padding: var(--padding-large);

        &.preview {
          flex-direction: column;
        }

        + .asset-item {
          border-top: 1px solid var(--border-color);
        }

        .file {
          display: flex;
          flex: 1;
        }

        .asset-info {
          align-items: center;
          flex: 1;

          b {
            color: var(--color-gray-900);
          }

          small {
            color: var(--color-gray-600);
            display: block;
            margin-top: var(--margin-xsmall);
          }
        }

        .asset-actions {
          align-items: center;
          display: flex;
          gap: 10px;

          .bulk-files {
            width: 100%;

            .file-item {
              align-items: center;
              display: flex;
              gap: 16px;
              padding: var(--padding-large) 0;

              + .file-item {
                border-top: 1px solid var(--border-color);
              }

              &:last-child {
                padding: var(--padding-large) 0 0 0;
              }

              .image-preview {
                cursor: pointer;

                &:hover {
                  opacity: 0.8;
                }

                img {
                  background-size: contain !important;
                  border: 1px solid var(--border-color);
                  border-radius: var(--border-radius-base);
                  display: block;
                  height: 120px;
                  width: auto;
                }
              }

              .icon-actions {
                display: flex;
                flex: 1;
                justify-content: end;
              }
            }
          }
        }
      }
    }
  }

  :global(.bulk-image-popover) {
    max-width: none;
  }

  @media only screen and (max-width: 800px) {
    .image-bulk-upload-form {
      .asset-details {
        .asset-item {
          display: flex;
          flex-direction: column;
          gap: 16px;

          .file {
            display: flex;
            flex-direction: column;
            gap: 16px;
          }

          .asset-actions {
            .bulk-files {
              .file-item {
                padding: var(--padding-base) 0;
              }
            }
          }
        }
      }
    }
  }
</style>
